import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PageProps } from './types';
import {
  Button,
  EmptyState,
  KindBadge,
  ProgressBar,
  WaveLane,
  WaveTrimmer,
  useClipPlayer,
  useContextPlayer,
} from '../components';
import {
  clearRemovalSuggestion,
  correctSegment,
  getIncorrectSegments,
  listTasks,
  reconcileTask,
  removeSegment,
  setSegmentVerify,
} from '../db';
import type { Segment, SegmentKind, Task } from '../types';
import { formatTimecode } from '../services/audioSlicer';
import {
  confirmMergedLabel,
  getNeighbours,
  mergeAdjacentSegments,
  type Neighbours,
} from '../services/segmentEdits';

/**
 * Correct tab — stage 3.
 *
 * WHAT THIS PAGE IS ACTUALLY FOR
 * -----------------------------
 * It labels the words that stage-2 Verify rejected. Verify judges a whole
 * SENTENCE at a time, and a "✗ Wrong" verdict marks EVERY word in that sentence
 * `incorrect` — so one tap upstream can fill this queue with a dozen cards, most
 * of which were fine. Typing the right word here is the primary job; everything
 * else on the card is secondary.
 *
 * Queue = every segment with `verify === 'incorrect'` that has no correction yet
 * (`getIncorrectSegments({ onlyUncorrected: true })`), grouped by task.
 *
 * Per segment you can:
 *   - play / replay its clip
 *   - tap the big `heard` word to type the real word (saves `correctedText`)
 *   - tap "Other" to open a sheet: Filler (Umm / Hmm / Uh) | Background noise | Unclear
 *     (saves `correctedText` + `correctedKind`)
 *   - tap "Was fine" to undo a mis-tap in Verify (flips back to `verify: 'correct'`)
 *   - REMOVE it, when the row should not exist at all (a stale duplicate, a
 *     label on a mouth click). Inline undo, then a real delete — see
 *     {@link UNDO_MS}.
 *   - MERGE it with the row before or after it in the task, when the two are one
 *     run of connected speech that cannot be cut between.
 *
 * Every save is followed by `reconcileTask(taskId)` so a task whose last open
 * segment just got fixed flips to `completed` and shows up in the Done tab.
 *
 * THIS IS THE OLDEST OF THE THREE REVIEW SCREENS
 * ----------------------------------------------
 * It predates the Cuts station, and it shows. Its three BOUNDARY affordances —
 * "✂ Adjust cut" (WaveTrimmer), the merge picker, and the removal flow — were
 * built when this page was the only place a human could fix a bad cut. Cuts (v2)
 * now owns that question and answers it with a different engine:
 *
 *   - here:        `services/segmentEdits.ts` (merge) + `boundaryEdits.ts`
 *                  (in-place: mutates rows, never obsoletes anything)
 *   - CutsPage:    `services/cutOps.ts` + `recut.ts`
 *                  (versioned: obsoletes inputs, mints named successor rows)
 *
 * Both uphold the partition invariant; they disagree about HISTORY. Do not add
 * boundary editing here — extend the Cuts station instead. See ARCHITECTURE.md
 * ("Two edit engines").
 *
 * NOT restructured in this pass (follow-up F7): the seven parallel selection-id
 * `useState` slots that should be one `cardMode` discriminated union, and the
 * ~230-line inline card that should be a `CorrectCard` component. The card list
 * is also un-virtualized, which matters more now than it did — sentence-level
 * Verify grows this queue roughly 8× faster per rejection than word-level did.
 */

/**
 * How long a removed card sits there offering an undo before it is committed.
 *
 * A delete is the only irreversible thing this app lets a human do, and it is
 * one tap away from the buttons they are hammering through a queue with. The
 * alternative — a confirm dialog — costs a tap on every genuine removal to guard
 * against the rare mis-tap, and on iOS a `confirm()` also kills the audio the
 * user is listening to. So: act immediately, keep the row on screen as its own
 * undo affordance, and commit a few seconds later.
 */
const UNDO_MS = 5000;

/* ------------------------------------------------------------------ */
/* "Other" picker options                                              */
/* ------------------------------------------------------------------ */

interface OtherOption {
  /** Button label. */
  label: string;
  /** Text written to `correctedText`. */
  text: string;
  /** Kind written to `correctedKind`. */
  kind: SegmentKind;
}

const FILLER_OPTIONS: OtherOption[] = [
  { label: 'Umm', text: 'Umm', kind: 'filler' },
  { label: 'Hmm', text: 'Hmm', kind: 'filler' },
  { label: 'Uh', text: 'Uh', kind: 'filler' },
];

const NON_SPEECH_OPTIONS: OtherOption[] = [
  { label: '🔊  Background noise', text: '[noise]', kind: 'noise' },
  { label: '❓  Unclear', text: '[unclear]', kind: 'unclear' },
];

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function confidencePct(c: number): string {
  const n = Number.isFinite(c) ? Math.round(Math.min(Math.max(c, 0), 1) * 100) : 0;
  return `${n}%`;
}

interface TaskGroup {
  taskId: string;
  name: string;
  segments: Segment[];
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

/** A task that just auto-reconciled, offered as a jump to its transcript. */
interface CompletionToast {
  taskId: string;
  message: string;
}

export default function CorrectPage({ onGoToTab, onOpenTask, focusTaskId }: PageProps) {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** How many segments this session has resolved — drives the progress bar. */
  const [fixedCount, setFixedCount] = useState(0);

  const [playingId, setPlayingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [sheetFor, setSheetFor] = useState<Segment | null>(null);
  /** Segment whose waveform trimmer is expanded inside its card, if any. */
  const [trimmingId, setTrimmingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<CompletionToast | null>(null);

  /** Cards showing "Removed — undo": still on screen, not yet deleted. */
  const [removingIds, setRemovingIds] = useState<string[]>([]);
  /** Segment whose merge strip is open, and the task neighbours it may fuse with. */
  const [mergeFor, setMergeFor] = useState<string | null>(null);
  const [neighbours, setNeighbours] = useState<Record<string, Neighbours>>({});
  /** A merge just happened here — the editor is open to confirm the joined label. */
  const [confirmingMergeId, setConfirmingMergeId] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const tasksRef = useRef<Task[]>([]);
  tasksRef.current = tasks;
  /** Pending removals: id -> timer. Flushed (committed) if the page unmounts. */
  const removalTimers = useRef(new Map<string, number>());

  const player = useClipPlayer(playingId, { autoPlay: true });

  /* ---------------- load ---------------- */

  const load = useCallback(async () => {
    setError(null);
    try {
      const [rows, allTasks] = await Promise.all([
        getIncorrectSegments({ onlyUncorrected: true }),
        listTasks(),
      ]);
      setSegments(rows);
      setTasks(allTasks);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load the correction queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* ---------------- toast auto-dismiss ---------------- */

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(t);
  }, [toast]);

  /* ---------------- focus the inline input ---------------- */

  useEffect(() => {
    if (editingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingId]);

  /* ---------------- escape closes the sheet ---------------- */

  useEffect(() => {
    if (!sheetFor) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSheetFor(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetFor]);

  /* ---------------- grouping ---------------- */

  const groups = useMemo<TaskGroup[]>(() => {
    const byTask = new Map<string, Segment[]>();
    for (const s of segments) {
      const bucket = byTask.get(s.taskId);
      if (bucket) bucket.push(s);
      else byTask.set(s.taskId, [s]);
    }
    for (const list of byTask.values()) list.sort((a, b) => a.index - b.index);

    const ordered: TaskGroup[] = [];
    const seen = new Set<string>();
    // `listTasks()` is newest-first, so groups follow the Tasks tab order.
    for (const t of tasks) {
      const list = byTask.get(t.id);
      if (!list) continue;
      ordered.push({ taskId: t.id, name: t.name, segments: list });
      seen.add(t.id);
    }
    for (const [taskId, list] of byTask) {
      if (!seen.has(taskId)) ordered.push({ taskId, name: 'Unknown task', segments: list });
    }

    // The queue stays cross-task; a hand-off from the Tasks tab only decides
    // which group the user lands on first.
    if (focusTaskId) {
      const at = ordered.findIndex((g) => g.taskId === focusTaskId);
      if (at > 0) ordered.unshift(...ordered.splice(at, 1));
    }
    return ordered;
  }, [segments, tasks, focusTaskId]);

  /* ---------------- context playback ---------------- */

  // The queue is cross-task but grouped, so the first group is what the user is
  // looking at — warm that task's original audio so the first "±1s" tap is
  // instant. Playing a card from a later group just loads that task on demand.
  const context = useContextPlayer(groups[0]?.taskId ?? null);

  // A card that just got resolved leaves the queue — its context playback must
  // not keep running under a card that is no longer on screen.
  const contextStop = context.stop;
  const contextActiveId = context.activeId;
  useEffect(() => {
    if (!contextActiveId) return;
    if (segments.some((s) => s.id === contextActiveId)) return;
    contextStop();
  }, [segments, contextActiveId, contextStop]);

  /* ---------------- actions ---------------- */

  const handlePlay = useCallback(
    (id: string) => {
      // Calling play() straight from the tap keeps the user gesture — iOS
      // Safari refuses playback started from anywhere else until the element
      // has been activated once (see `useClipPlayer`'s unlock).
      context.stop();
      if (playingId !== id) setPlayingId(id);
      else if (player.isPlaying) player.stop();
      else void player.replay();
    },
    [playingId, player, context],
  );

  /** Play the segment with a second of lead-in and tail from the source file. */
  const handleContext = useCallback(
    (seg: Segment) => {
      if (context.activeId === seg.id && context.isPlaying) {
        context.stop();
        return;
      }
      player.stop();
      void context.play(seg);
    },
    [context, player],
  );

  /* ---------------- waveform trimmer ---------------- */

  /**
   * Open/close the inline trim editor. Opening takes the room: both players
   * stop, so the trimmer's own playback is the only audio running.
   */
  const toggleTrimmer = useCallback(
    (seg: Segment) => {
      context.stop();
      player.stop();
      setPlayingId(null);
      setTrimmingId((cur) => (cur === seg.id ? null : seg.id));
    },
    [context, player],
  );

  /**
   * New boundaries landed. Update the row in place so the timecode is honest,
   * and drop `playingId` — the stored clip blob was replaced, and the clip
   * player only re-reads it when the segment it is pointed at changes.
   */
  const handleTrimSaved = useCallback((seg: Segment, next: { startSec: number; endSec: number }) => {
    setSegments((prev) =>
      prev.map((s) =>
        s.id === seg.id ? { ...s, startSec: next.startSec, endSec: next.endSec, confidence: 1 } : s,
      ),
    );
    setPlayingId((cur) => (cur === seg.id ? null : cur));
  }, []);

  /* ---------------- waveform lane ---------------- */

  /** Task length per id, so a lane can size its window before the decode lands. */
  const durationByTask = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of tasks) m[t.id] = t.durationSec;
    return m;
  }, [tasks]);

  /** Open (never toggle) the real trimmer — the lane's edges hand off to it. */
  const openTrimmer = useCallback(
    (segmentId: string) => {
      context.stop();
      player.stop();
      setPlayingId(null);
      setTrimmingId(segmentId);
    },
    [context, player],
  );

  /**
   * A drag-to-align landed (or was undone). Patch the rows in place so the
   * timecodes stay honest, and let go of any clip player pointed at a segment
   * whose blob was just replaced.
   */
  const handleLaneMoved = useCallback((moves: Array<{ id: string; startSec: number; endSec: number }>) => {
    const byId = new Map(moves.map((m) => [m.id, m]));
    setSegments((prev) =>
      prev.map((s) => {
        const m = byId.get(s.id);
        return m ? { ...s, startSec: m.startSec, endSec: m.endSec } : s;
      }),
    );
    setPlayingId((cur) => (cur && byId.has(cur) ? null : cur));
  }, []);

  /** Drop a resolved segment out of the queue and report task completion. */
  const finishSegment = useCallback((seg: Segment, completed: boolean) => {
    setSegments((prev) => prev.filter((s) => s.id !== seg.id));
    setFixedCount((c) => c + 1);
    setTrimmingId((cur) => (cur === seg.id ? null : cur));
    setEditingId((cur) => (cur === seg.id ? null : cur));
    setSheetFor((cur) => (cur && cur.id === seg.id ? null : cur));
    setPlayingId((cur) => (cur === seg.id ? null : cur));
    if (completed) {
      const name = tasksRef.current.find((t) => t.id === seg.taskId)?.name ?? 'Task';
      setToast({ taskId: seg.taskId, message: `“${name}” is complete — it moved to Done.` });
    }
  }, []);

  const saveCorrection = useCallback(
    async (seg: Segment, correction: { text?: string; kind?: SegmentKind }) => {
      setBusyId(seg.id);
      setError(null);
      try {
        const saved = await correctSegment(seg.id, correction);
        if (!saved) {
          // blank text and no kind — nothing was written, so the segment must
          // stay in the queue instead of vanishing until the next reload
          setError('That correction was empty — type a word or pick a label.');
          return;
        }
        const status = await reconcileTask(seg.taskId);
        finishSegment(seg, status === 'completed');
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Could not save that correction.');
      } finally {
        setBusyId(null);
      }
    },
    [finishSegment],
  );

  /* ---------------- remove ---------------- */

  /**
   * Commit a pending removal. Deliberately independent of React state so the
   * unmount flush can call it: a card the user has already seen turn into
   * "Removed" must not come back to life because they switched tabs.
   */
  const commitRemoval = useCallback(
    async (seg: Segment) => {
      removalTimers.current.delete(seg.id);
      try {
        const result = await removeSegment(seg.id);
        setSegments((prev) => prev.filter((s) => s.id !== seg.id));
        setRemovingIds((prev) => prev.filter((id) => id !== seg.id));
        setFixedCount((c) => c + 1);
        if (result?.status === 'completed') {
          const name = tasksRef.current.find((t) => t.id === seg.taskId)?.name ?? 'Task';
          setToast({ taskId: seg.taskId, message: `“${name}” is complete — it moved to Done.` });
        }
      } catch (err: unknown) {
        setRemovingIds((prev) => prev.filter((id) => id !== seg.id));
        setError(err instanceof Error ? err.message : 'Could not remove that segment.');
      }
    },
    [],
  );

  /** Start the undo window. Nothing is written yet. */
  const requestRemoval = useCallback(
    (seg: Segment) => {
      context.stop();
      player.stop();
      setPlayingId((cur) => (cur === seg.id ? null : cur));
      setTrimmingId((cur) => (cur === seg.id ? null : cur));
      setEditingId((cur) => (cur === seg.id ? null : cur));
      setMergeFor((cur) => (cur === seg.id ? null : cur));
      setRemovingIds((prev) => (prev.includes(seg.id) ? prev : [...prev, seg.id]));
      const timer = window.setTimeout(() => void commitRemoval(seg), UNDO_MS);
      removalTimers.current.set(seg.id, timer);
    },
    [commitRemoval, context, player],
  );

  const undoRemoval = useCallback((seg: Segment) => {
    const timer = removalTimers.current.get(seg.id);
    if (timer !== undefined) window.clearTimeout(timer);
    removalTimers.current.delete(seg.id);
    setRemovingIds((prev) => prev.filter((id) => id !== seg.id));
  }, []);

  // Leaving the tab commits whatever the undo window was still holding. The
  // timers die with the page, so the alternative is a row the user removed
  // quietly reappearing on the next visit.
  const segmentsRef = useRef<Segment[]>([]);
  segmentsRef.current = segments;
  useEffect(() => {
    const timers = removalTimers.current;
    const rows = segmentsRef;
    return () => {
      for (const [id, timer] of timers) {
        window.clearTimeout(timer);
        const seg = rows.current.find((s) => s.id === id);
        if (seg) void removeSegment(seg.id).catch(() => undefined);
      }
      timers.clear();
    };
  }, []);

  /* ---------------- merge ---------------- */

  const toggleMerge = useCallback(
    async (seg: Segment) => {
      setMergeFor((cur) => (cur === seg.id ? null : seg.id));
      if (neighbours[seg.id]) return;
      try {
        const found = await getNeighbours(seg);
        setNeighbours((prev) => ({ ...prev, [seg.id]: found }));
      } catch {
        setNeighbours((prev) => ({ ...prev, [seg.id]: {} }));
      }
    },
    [neighbours],
  );

  const doMerge = useCallback(
    async (seg: Segment, direction: 'prev' | 'next') => {
      setBusyId(seg.id);
      setError(null);
      try {
        const out = await mergeAdjacentSegments(seg.id, direction);
        // The absorbed row may itself have been in this queue.
        setSegments((prev) =>
          prev
            .filter((s) => s.id !== out.removedId)
            .map((s) => (s.id === out.merged.id ? out.merged : s)),
        );
        setNeighbours({});
        setMergeFor(null);
        setPlayingId(null);
        setTrimmingId(null);
        // Offer the label straight away: the merged text is two machine guesses
        // with a space in it until a human says otherwise.
        setConfirmingMergeId(out.merged.id);
        setEditingId(out.merged.id);
        setDraft(out.merged.correctedText ?? out.merged.heard);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Could not merge those segments.');
      } finally {
        setBusyId(null);
      }
    },
    [],
  );

  /** Escape hatch for a mis-tap in Verify: put the segment back to `correct`. */
  const markWasFine = useCallback(
    async (seg: Segment) => {
      setBusyId(seg.id);
      setError(null);
      try {
        // "It was fine" also overrules the machine's duplicate suspicion — the
        // hint must not be waiting on the card if the row ever comes back.
        if (seg.suggestedRemoval) await clearRemovalSuggestion(seg.id);
        await setSegmentVerify(seg.id, 'correct');
        const status = await reconcileTask(seg.taskId);
        finishSegment(seg, status === 'completed');
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Could not update that segment.');
      } finally {
        setBusyId(null);
      }
    },
    [finishSegment],
  );

  const startEditing = useCallback((seg: Segment) => {
    setSheetFor(null);
    // the trimmer lives in the same card slot as the editor — one at a time
    setTrimmingId(null);
    setMergeFor(null);
    setConfirmingMergeId(null);
    setEditingId(seg.id);
    setDraft(seg.correctedText ?? seg.heard);
  }, []);

  /**
   * Confirm the label of a span the user just merged. Separate from
   * `saveCorrection` because this is the one edit that also means "and I
   * listened to it": see `confirmMergedLabel` for why that buys confidence 1.
   */
  const confirmMerge = useCallback(
    async (seg: Segment, text: string) => {
      setBusyId(seg.id);
      setError(null);
      try {
        const { status } = await confirmMergedLabel(seg.id, text);
        setConfirmingMergeId(null);
        finishSegment(seg, status === 'completed');
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Could not save that label.');
      } finally {
        setBusyId(null);
      }
    },
    [finishSegment],
  );

  const submitDraft = useCallback(
    (seg: Segment) => {
      const text = draft.trim();
      if (!text) return;
      if (confirmingMergeId === seg.id) {
        void confirmMerge(seg, text);
        return;
      }
      // Typed corrections only change the text — the model's `kind` stays put
      // unless the user explicitly picks one from the "Other" sheet.
      void saveCorrection(seg, { text });
    },
    [draft, saveCorrection, confirmingMergeId, confirmMerge],
  );

  /* ---------------- render ---------------- */

  const remaining = segments.length;
  const totalForBar = remaining + fixedCount;

  return (
    <>
      <header className="page__head">
        <h1 className="page__title">Correct</h1>
        <span className="page__sub">
          {loading ? 'loading…' : remaining === 0 ? 'all clear' : `${remaining} to fix`}
        </span>
      </header>

      {totalForBar > 0 && (
        <div className="cp-topbar">
          <ProgressBar label="Fixed" value={fixedCount} max={totalForBar} />
        </div>
      )}

      {error && (
        <div className="cp-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <div className="cp-loading">Loading corrections…</div>
      ) : remaining === 0 ? (
        <EmptyState
          icon="✏️"
          title={fixedCount > 0 ? 'Correction queue cleared' : 'Nothing to correct'}
          hint={
            fixedCount > 0
              ? 'Every segment you marked incorrect now has a label.'
              : 'Segments you mark incorrect in the Verify tab show up here.'
          }
          action={
            <Button variant="primary" onClick={() => onGoToTab(fixedCount > 0 ? 'done' : 'verify')}>
              {fixedCount > 0 ? 'Go to Done' : 'Go to Verify'}
            </Button>
          }
        />
      ) : (
        groups.map((group) => (
          <section key={group.taskId} className="cp-group">
            <h2 className="cp-group__head">
              <span className="cp-group__name">{group.name}</span>
              <span className="cp-group__count mono">{group.segments.length}</span>
            </h2>

            {group.segments.map((seg) => {
              const isEditing = editingId === seg.id;
              const isCurrent = playingId === seg.id;
              const ctxOn = context.activeId === seg.id;
              const busy = busyId === seg.id;
              const trimOn = trimmingId === seg.id;
              const mergeOn = mergeFor === seg.id;
              const near = neighbours[seg.id];

              if (removingIds.includes(seg.id)) {
                return (
                  <article key={seg.id} className="card cp-card cp-card--removed" aria-live="polite">
                    <span className="cp-removed__text">
                      Removed <strong>{seg.heard || '—'}</strong>
                    </span>
                    <Button size="sm" onClick={() => undoRemoval(seg)}>
                      ↩ Undo
                    </Button>
                  </article>
                );
              }

              return (
                <article key={seg.id} className="card cp-card">
                  <div className="cp-card__meta">
                    <KindBadge kind={seg.kind} />
                    <span className="cp-conf mono">{confidencePct(seg.confidence)}</span>
                    <span className="spacer" />
                    <span className="cp-time mono">
                      {formatTimecode(seg.startSec)}–{formatTimecode(seg.endSec)}
                    </span>
                  </div>

                  {isEditing ? (
                    <form
                      className="cp-edit"
                      onSubmit={(e) => {
                        e.preventDefault();
                        submitDraft(seg);
                      }}
                    >
                      {confirmingMergeId === seg.id && (
                        <div className="cp-merge__hint">
                          Merged. Play it, then confirm what the whole clip says.
                        </div>
                      )}
                      <input
                        ref={inputRef}
                        className="cp-input"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            setEditingId(null);
                          }
                        }}
                        placeholder="type the correct word"
                        aria-label={`Correction for “${seg.heard}”`}
                        autoComplete="off"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        enterKeyHint="done"
                      />
                      <div className="cp-actions">
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={busy || draft.trim().length === 0}
                        >
                          {busy ? 'Saving…' : confirmingMergeId === seg.id ? 'Confirm label' : 'Save'}
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setEditingId(null);
                            setConfirmingMergeId((cur) => (cur === seg.id ? null : cur));
                          }}
                          disabled={busy}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <>
                      {/*
                        LEGACY-FALLBACK ONLY — do not remove in a dead-code sweep.

                        `suggestedRemoval` has exactly one writer in the whole
                        app: `services/legacy/duplicateResolve.ts`, which runs
                        only in the legacy pipeline (no aligner/WhisperX helper
                        reachable — i.e. every phone). A task processed through
                        the aligned path never sets it, so this block is dead on
                        a developer's machine with the helpers running and LIVE
                        for most real users. Judging it by what renders locally
                        will delete a feature that is in daily use.

                        Only `.hint` is ever read. `.reason`, `.counterpartId`
                        and `.at` are write-only — see `types.ts`.
                      */}
                      {seg.suggestedRemoval && (
                        <div className="cp-suggest">
                          <span className="cp-suggest__text">
                            <span aria-hidden="true">⚠ </span>
                            {seg.suggestedRemoval.hint}
                          </span>
                          <Button
                            size="sm"
                            className="cp-rmbtn"
                            onClick={() => requestRemoval(seg)}
                            disabled={busy}
                          >
                            🗑 Remove
                          </Button>
                        </div>
                      )}

                      <button
                        type="button"
                        className="cp-heard"
                        onClick={() => startEditing(seg)}
                        disabled={busy}
                      >
                        <span className="cp-heard__text">{seg.heard || '—'}</span>
                        <span className="cp-heard__hint">tap to type the correct word</span>
                      </button>

                      {seg.contextual && seg.contextual !== seg.heard && (
                        <div className="cp-contextual">
                          in context: <strong>{seg.contextual}</strong>
                        </div>
                      )}

                      {/* A card list cannot afford thirty waveforms, so this
                          starts as a tile-only strip (no decode) and becomes a
                          full lane — waveform, legend, drag-to-align — on tap. */}
                      <WaveLane
                        variant="strip"
                        taskId={seg.taskId}
                        currentSegmentId={seg.id}
                        durationSec={durationByTask[seg.taskId]}
                        editable
                        playheads={[
                          {
                            kind: 'clip',
                            ref: player.audioRef,
                            active: isCurrent && player.isPlaying,
                            segmentId: seg.id,
                            clipDurSec: player.durationSec,
                          },
                          {
                            kind: 'file',
                            ref: context.audioRef,
                            active: ctxOn && context.isPlaying,
                          },
                        ]}
                        onRequestTrim={openTrimmer}
                        onMoved={handleLaneMoved}
                      />

                      <div className="cp-actions">
                        <Button
                          size="sm"
                          onClick={() => handlePlay(seg.id)}
                          icon={isCurrent && player.isPlaying ? '⏸' : '▶'}
                          disabled={busy || (isCurrent && player.isLoading)}
                        >
                          {isCurrent && player.isPlaying ? 'Pause' : isCurrent ? 'Replay' : 'Play'}
                        </Button>
                        <Button
                          size="sm"
                          className={`cp-ctxbtn${ctxOn && context.isPlaying ? ' is-on' : ''}`}
                          onClick={() => handleContext(seg)}
                          aria-pressed={ctxOn && context.isPlaying}
                          aria-label="Play with one second before and after"
                          title="Play with 1s before and after"
                          disabled={busy}
                        >
                          {ctxOn && context.isLoading ? '…' : '±1s'}
                        </Button>
                        <Button size="sm" onClick={() => setSheetFor(seg)} disabled={busy}>
                          Other…
                        </Button>
                        <Button
                          size="sm"
                          className={`cp-ctxbtn${trimOn ? ' is-on' : ''}`}
                          onClick={() => toggleTrimmer(seg)}
                          aria-pressed={trimOn}
                          aria-expanded={trimOn}
                          aria-label="Adjust where this clip is cut"
                          title="Adjust the cut on the waveform"
                          disabled={busy}
                        >
                          ✂ Adjust cut
                        </Button>
                        <Button
                          size="sm"
                          className={`cp-ctxbtn${mergeOn ? ' is-on' : ''}`}
                          onClick={() => void toggleMerge(seg)}
                          aria-pressed={mergeOn}
                          aria-expanded={mergeOn}
                          aria-label="Merge this segment with the one before or after it"
                          title="Join with the segment before or after"
                          disabled={busy}
                        >
                          ⇔ Merge
                        </Button>
                        <Button
                          size="sm"
                          className="cp-rmbtn"
                          onClick={() => requestRemoval(seg)}
                          aria-label="Remove this segment — it should not exist"
                          disabled={busy}
                        >
                          🗑 Remove
                        </Button>
                        <span className="spacer" />
                        <button
                          type="button"
                          className="cp-linkbtn"
                          onClick={() => void markWasFine(seg)}
                          disabled={busy}
                        >
                          Was fine
                        </button>
                      </div>

                      {mergeOn && (
                        <div className="cp-merge">
                          <div className="cp-merge__hint">
                            Join into one clip — for speech that runs together.
                          </div>
                          <Button
                            block
                            disabled={busy || !near?.prev}
                            onClick={() => void doMerge(seg, 'prev')}
                          >
                            ⬅ With previous
                            {near?.prev ? ` · “${near.prev.heard || '—'}”` : near ? ' · none' : '…'}
                          </Button>
                          <Button
                            block
                            disabled={busy || !near?.next}
                            onClick={() => void doMerge(seg, 'next')}
                          >
                            ➡ With next
                            {near?.next ? ` · “${near.next.heard || '—'}”` : near ? ' · none' : '…'}
                          </Button>
                        </div>
                      )}

                      {isCurrent && player.error && <div className="cp-cliperr">{player.error}</div>}
                      {ctxOn && context.error && <div className="cp-cliperr">{context.error}</div>}

                      {trimOn && (
                        <WaveTrimmer
                          segment={seg}
                          label={seg.heard || seg.contextual}
                          onSaved={(next) => handleTrimSaved(seg, next)}
                          onClose={() => setTrimmingId((cur) => (cur === seg.id ? null : cur))}
                        />
                      )}
                    </>
                  )}
                </article>
              );
            })}
          </section>
        ))
      )}

      {sheetFor && (
        <div
          className="cp-sheet__backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSheetFor(null);
          }}
        >
          <div className="cp-sheet" role="dialog" aria-modal="true" aria-label="Pick a label">
            <div className="cp-sheet__grip" aria-hidden="true" />
            <div className="cp-sheet__title">
              What is <strong>{sheetFor.heard || 'this'}</strong> really?
            </div>

            <div className="cp-sheet__label">Filler</div>
            <div className="cp-chips">
              {FILLER_OPTIONS.map((opt) => (
                <Button
                  key={opt.text}
                  className="cp-chip"
                  disabled={busyId === sheetFor.id}
                  onClick={() => void saveCorrection(sheetFor, { text: opt.text, kind: opt.kind })}
                >
                  {opt.label}
                </Button>
              ))}
            </div>

            <div className="cp-sheet__label">Not a word</div>
            <div className="col">
              {NON_SPEECH_OPTIONS.map((opt) => (
                <Button
                  key={opt.text}
                  block
                  disabled={busyId === sheetFor.id}
                  onClick={() => void saveCorrection(sheetFor, { text: opt.text, kind: opt.kind })}
                >
                  {opt.label}
                </Button>
              ))}
            </div>

            <div className="cp-sheet__foot">
              <Button variant="ghost" block onClick={() => setSheetFor(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="cp-toast" role="status">
          <span>{toast.message}</span>
          <button
            type="button"
            className="cp-toast__btn"
            onClick={() => onOpenTask(toast.taskId, 'done')}
          >
            View
          </button>
        </div>
      )}
    </>
  );
}
